import { useState, useEffect } from 'react';
import { getEvents, createEvent, updateEvent, deleteEvent } from '../services/event.service';
import { Event } from '../types/event';
import { useToast } from '../hooks/useToast';

export const useEvents = (filters: any = {}) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const data = await getEvents(filters);
        setEvents(data);
        setError(null);
      } catch (err: any) {
        setError(err);
        showToast('Erreur lors du chargement des événements', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [JSON.stringify(filters)]);

  const addEvent = async (event: Partial<Event>) => {
    try {
      setLoading(true);
      const newEvent = await createEvent(event);
      setEvents(prev => [...prev, newEvent]);
      showToast('Événement créé avec succès', 'success');
      return newEvent;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors de la création de l\'événement', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const editEvent = async (id: string, event: Partial<Event>) => {
    try {
      setLoading(true);
      const updatedEvent = await updateEvent(id, event);
      setEvents(prev => prev.map(e => e.id === id ? updatedEvent : e));
      showToast('Événement mis à jour avec succès', 'success');
      return updatedEvent;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors de la mise à jour de l\'événement', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeEvent = async (id: string) => {
    try {
      setLoading(true);
      await deleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      showToast('Événement supprimé avec succès', 'success');
      return true;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors de la suppression de l\'événement', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { events, loading, error, addEvent, editEvent, removeEvent };
};
